import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Container, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import { ArrowLeft } from "react-bootstrap-icons";
import { useApp } from "app/contexts/AppContext";
import { updateUserProfile, uploadImage, clearUpdateSuccess } from "./slice";
import BasicInfo from "./BasicInfo";
import InspectorLicense from "./InspectorLicense";
import CompanyInfo from "./CompanyInfo";
import Account from "./Account";
import CalendarConnection from "./CalendarConnection";
import PaymentConnection from "app/containers/PaymentConnection";
import CustomToast from "app/components/common/CustomToast";
import {
  getInitialFormData,
  mapProfileToFormData,
  validateForm,
  sanitizeFormData,
  renderError,
  getUserTimezone,
} from "./helperFunctions";
import "./userInfo.css";

const UserInfo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    isLoading,
    error,
    updateSuccess,
    imageUploadingFields,
    imageUploadError,
    uploadedImages,
    isCalendarProvidersLoading,
  } = useSelector((state) => state.userProfile);
  const {
    device: { isMobile },
    user: { profile, updateUserProfile: updateAppUserProfile },
  } = useApp();

  const [searchParams, setSearchParams] = useSearchParams();
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData());
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("User information updated");
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const location = useLocation();
  // Section to scroll to once the calendar providers have loaded: that card
  // grows when they arrive, which would push any section below it out of
  // place. Set by the OAuth return or by a navigation's state.scrollTo.
  const pendingScrollTarget = useRef(location.state?.scrollTo || null);
  const providersLoadStarted = useRef(false);
  const scrollTimer = useRef(null);

  // Helper function to check if a specific field is uploading
  const isFieldUploading = (fieldName) => {
    return imageUploadingFields[fieldName] || false;
  };

  // Helper function to check if any image is uploading (for form submission)
  const isAnyImageUploading = () => {
    return Object.values(imageUploadingFields).some(
      (isUploading) => isUploading
    );
  };

  useEffect(() => {
    const mappedData = mapProfileToFormData(profile);

    // Preselect timezone if not already set in profile
    if (!mappedData.timezone) {
      const userTimezone = getUserTimezone();
      mappedData.timezone = userTimezone;
    }

    setFormData(mappedData);
  }, [profile]);

  // Handle successful profile update
  useEffect(() => {
    if (updateSuccess) {
      setToastMessage("User information updated");
      setShowSuccessToast(true);
      dispatch(clearUpdateSuccess());
    }
  }, [updateSuccess, dispatch]);

  // Handle OAuth redirect callback
  useEffect(() => {
    const oauthStatus = searchParams.get("oauth");
    if (!oauthStatus) return;

    if (oauthStatus === "success") {
      setToastMessage("Account connected successfully");
      setShowSuccessToast(true);
      pendingScrollTarget.current = "calendar-connection";
    } else if (oauthStatus === "error") {
      const rawReason = searchParams.get("reason");
      const reason = rawReason
        ? rawReason.slice(0, 200)
        : "Failed to connect calendar account.";
      setErrorToastMessage(reason);
      setShowErrorToast(true);
    }

    // Clean only OAuth-related params without affecting others
    searchParams.delete("oauth");
    searchParams.delete("reason");
    setSearchParams(searchParams, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear the state so a reload doesn't scroll again.
  useEffect(() => {
    if (location.state?.scrollTo) {
      window.history.replaceState({}, document.title);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The loading flag starts false and CalendarConnection only starts the fetch
  // in its own mount effect, so "not loading" means "done" only once a load
  // has been seen to start.
  useEffect(() => {
    if (isCalendarProvidersLoading) {
      providersLoadStarted.current = true;
      return;
    }
    const target = pendingScrollTarget.current;
    if (!providersLoadStarted.current || !target) return;
    pendingScrollTarget.current = null;
    // Lets the card swap its spinner for the list before measuring.
    scrollTimer.current = setTimeout(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
    }, 300);
  }, [isCalendarProvidersLoading]);

  useEffect(() => () => clearTimeout(scrollTimer.current), []);

  // Scroll to validation alert when it appears
  useEffect(() => {
    if (showValidationAlert) {
      const alertElement = document.getElementById("validation-alert");
      if (alertElement) {
        alertElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [showValidationAlert]);

  // Handle uploaded images
  useEffect(() => {
    // Update form data with uploaded image IDs
    Object.entries(uploadedImages).forEach(([fieldName, imageData]) => {
      if (fieldName === "company_logo") {
        // Handle company logo - API expects company_profile_image
        setFormData((prev) => ({
          ...prev,
          company_profile_image: imageData.id,
        }));
      } else {
        // Handle regular fields like profile_image
        setFormData((prev) => ({
          ...prev,
          [fieldName]: imageData.id,
        }));
      }
    });
  }, [uploadedImages]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name.includes(".")) {
      // Handle nested object properties (e.g., "user_address.city")
      const [parentKey, childKey] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parentKey]: {
          ...prev[parentKey],
          [childKey]: value,
        },
      }));
    } else {
      // Handle regular properties
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleImageUpload = (fieldName) => async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setValidationErrors((prev) => ({
          ...prev,
          [fieldName]: "File size must be less than 5MB",
        }));
        return;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        setValidationErrors((prev) => ({
          ...prev,
          [fieldName]: "Please select a valid image file",
        }));
        return;
      }

      // Clear any existing validation error for this field
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });

      // Upload image to server
      try {
        const imageFormData = new FormData();
        imageFormData.append("image", file);

        await dispatch(
          uploadImage({ imageData: imageFormData, fieldName })
        ).unwrap();
      } catch (error) {
        // Extract error message from API response
        let errorMessage = "Failed to upload image. Please try again."; // Default fallback

        if (error && Array.isArray(error) && error.length > 0) {
          // Handle array of error messages
          errorMessage = error[0];
        } else if (error && typeof error === "string") {
          // Handle string error message
          errorMessage = error;
        } else if (error && error.message) {
          // Handle error object with message property
          errorMessage = error.message;
        } else if (error && error.detail) {
          // Handle error object with detail property
          errorMessage = error.detail;
        }

        setValidationErrors((prev) => ({
          ...prev,
          [fieldName]: errorMessage,
        }));
      }
    }
  };

  /**
   * Handle image click to trigger file input
   * @param {string} fieldName - The form field name for the image
   * @returns {Function} - Click handler function
   */
  const handleImageClick = (fieldName) => (e) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling

    // Additional safety check
    if (e.target.tagName === "BUTTON" && e.target.type !== "button") {
      e.target.type = "button";
    }

    // Find and click the corresponding file input
    const fileInput = document.getElementById(`${fieldName}-input`);
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setShowValidationAlert(true);
      // Scroll to alert after state updates
      setTimeout(() => {
        const alertElement = document.getElementById("validation-alert");
        if (alertElement) {
          alertElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 0);
      return;
    }

    // Clear validation errors
    setValidationErrors({});
    setShowValidationAlert(false);

    // Sanitize form data - no need to handle files here since they're uploaded separately
    const sanitizedData = sanitizeFormData(formData);

    try {
      const result = await dispatch(updateUserProfile(sanitizedData)).unwrap();

      // Update the user profile in AppContext with the response data
      if (result && updateAppUserProfile) {
        if (result.user) {
          updateAppUserProfile(result.user);
        } else if (result.data && result.data.user) {
          updateAppUserProfile(result.data.user);
        } else {
          // If the response is the user object itself
          updateAppUserProfile(result);
        }
      }

      // The route guard parks people here when their account has no Company,
      // recording where they were headed. Now that the save created one, finish
      // the trip instead of stranding them on this form.
      const returnTo = location.state?.from;
      if (returnTo) {
        navigate(returnTo, { replace: true });
      }
    } catch (error) {
      // Error handling is managed by Redux state
    }
  };

  const SaveFormButton = (
    <Button
      type="submit"
      variant="primary"
      disabled={isLoading || isAnyImageUploading()}
      onClick={handleSubmit}
      style={{
        minWidth: "120px",
        fontWeight: "500",
      }}
    >
      {isLoading ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="loading-button-spinner"
            disabled={isAnyImageUploading() || isLoading}
          />
        </>
      ) : (
        "Save"
      )}
    </Button>
  );

  return (
    <Container className="py-4 user-info-container">
      {/* Back Button. Hidden while the account has no Company: the route guard
          in routes/index.js would bounce them straight back here, so the button
          would look broken. The message replaces it with the reason. */}
      {profile?.company ? (
        <div className="back-button mb-3">
          <Button
            variant="link"
            onClick={() => navigate("/")}
            className="p-0 text-decoration-none back-link"
          >
            <ArrowLeft size={16} className="me-2" />
            Back to Dashboard
          </Button>
        </div>
      ) : (
        <Alert variant="info" className="mb-3">
          Add your company below to finish setting up your account. Your
          dashboard, jobs and payments open up once you save it.
        </Alert>
      )}

      {/* Title and Save Button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">User Information</h1>
        {!isMobile && SaveFormButton}
      </div>

      {/* Validation Alert */}
      {showValidationAlert && Object.keys(validationErrors).length > 0 && (
        <Alert
          id="validation-alert"
          variant="danger"
          dismissible
          onClose={() => setShowValidationAlert(false)}
          style={{ scrollMarginTop: "80px" }}
        >
          <Alert.Heading>Please fix the following errors:</Alert.Heading>
          <ul className="mb-0">
            {Object.entries(validationErrors).map(([field, message]) => (
              <li key={field}>{message}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* API Error */}
      {error && <Alert variant="danger">{renderError(error)}</Alert>}
      {imageUploadError && (
        <Alert variant="danger">{renderError(imageUploadError)}</Alert>
      )}

      <Form onSubmit={handleSubmit} className="user-info-form">
        <Card>
          <Card.Body>
            <BasicInfo
              formData={formData}
              handleInputChange={handleInputChange}
              handleImageUpload={handleImageUpload}
              handleImageClick={handleImageClick}
              profile={profile}
              validationErrors={validationErrors}
              isImageUploading={isFieldUploading("profile_image")}
              uploadedImageUrl={uploadedImages.profile_image?.url}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <InspectorLicense
              formData={formData}
              handleInputChange={handleInputChange}
              validationErrors={validationErrors}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <CompanyInfo
              formData={formData}
              handleInputChange={handleInputChange}
              handleImageUpload={handleImageUpload}
              handleImageClick={handleImageClick}
              profile={profile}
              validationErrors={validationErrors}
              isImageUploading={isFieldUploading("company_logo")}
              uploadedImageUrl={uploadedImages.company_logo?.url}
            />
          </Card.Body>
        </Card>

        <Card id="calendar-connection">
          <Card.Body>
            <CalendarConnection />
          </Card.Body>
        </Card>

        <PaymentConnection />

        <Card>
          <Card.Body>
            <Account
              formData={formData}
              handleInputChange={handleInputChange}
              validationErrors={validationErrors}
            />
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-end">{SaveFormButton}</div>
      </Form>

      {/* Success Toast */}
      <CustomToast
        show={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        title="Success"
        message={toastMessage}
        variant="success"
        position="bottom-end"
        isMobile={isMobile}
        autohide={true}
      />
      <CustomToast
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        title="Error"
        message={errorToastMessage}
        variant="danger"
        position="bottom-end"
        isMobile={isMobile}
        autohide={true}
      />
    </Container>
  );
};

export default UserInfo;
